import type { ReactNode } from "react";
import { createStore, useStore } from "zustand";
import type {
  DeviceKind,
  DeviceContextState,
  InitializationState,
} from "./types";
import type { UserMediaMonitor } from "./lib/track-monitor";

export const userMediaStore = createStore<UserMediaContext>(() => ({
  userMedia: {
    fetchStatus: "idle",
    dataStatus: "idle",
    data: undefined,
  },
  devices: {
    audio: {
      health: { status: "idle", error: null, lastActive: null },
      activeDeviceId: null,
      availableDevices: [],
      fetchStatus: "idle",
      dataStatus: "idle",
      stream: null,
    },
    video: {
      health: { status: "idle", error: null, lastActive: null },
      activeDeviceId: null,
      availableDevices: [],
      fetchStatus: "idle",
      dataStatus: "idle",
      stream: null,
    },
    speaker: {
      health: { status: "idle", error: null, lastActive: null },
      activeDeviceId: null,
      availableDevices: [],
      fetchStatus: "idle",
      dataStatus: "idle",
      stream: null,
    },
  },
  initializationState: {
    audio: "idle",
    video: "idle",
  },
  monitor: null,
  abortController: new AbortController(),
}));

type UserMediaContext = {
  userMedia: {
    fetchStatus: "idle" | "fetching" | "success" | "error";
    dataStatus: "idle" | "loading" | "success" | "error";
    data: MediaStream | undefined;
  };
  devices: DeviceContextState;
  initializationState: InitializationState;
  abortController: AbortController;
  monitor: UserMediaMonitor | null;
};

export type MediaEventType =
  | "device-changed"
  | "device-unreliable";

export type MediaEventPayload = {
  kind?: DeviceKind;
  deviceId?: string | null;
  deviceLabel?: string | null;
  isBluetooth?: boolean;
  isNewDevice?: boolean;
  previousDeviceId?: string | null;
  message?: string;
  reason?: string;
};

const mediaEventTarget = new EventTarget();

export const emitMediaEvent = (
  type: MediaEventType,
  payload: MediaEventPayload,
) => {
  mediaEventTarget.dispatchEvent(new CustomEvent(type, { detail: payload }));
};

export const subscribeMediaEvent = (
  type: MediaEventType,
  handler: (payload: MediaEventPayload) => void,
) => {
  const wrapped = (event: Event) => {
    handler((event as CustomEvent<MediaEventPayload>).detail);
  };
  mediaEventTarget.addEventListener(type, wrapped as EventListener);
  return () =>
    mediaEventTarget.removeEventListener(type, wrapped as EventListener);
};

export const UserMediaMonitorProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  return <>{children}</>;
};

export const useMediaMonitor = () => {
  return useStore(userMediaStore, (s) => s.monitor);
};

export const useUserMediaStream = () => {
  return useStore(userMediaStore, (s) => s.userMedia.data);
};
